import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from "../../components/images/logo-transparent-png.png";
import { toast } from "react-hot-toast";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import LockIcon from "@mui/icons-material/Lock";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

function ResetPassword() {
    const { id, token } = useParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [touched, setTouched] = useState({ password: false, confirmPassword: false });
    const [isValidToken, setIsValidToken] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const apiUrl = `${process.env.REACT_APP_API}`;
    const navigate = useNavigate();

    useEffect(() => {
        // Verify token validity
        axios
            .get(`${apiUrl}/resetpassword/${id}/${token}`)
            .then((response) => {
                setIsValidToken(true);
            })
            .catch((error) => {
                console.error(error);
                setIsValidToken(false);
                toast.error("Invalid or expired reset link");
            });
    }, [id, token]);

    // Password validation rules
    const passwordRules = [
        { test: (pw) => pw.length >= 8, message: "At least 8 characters" },
        { test: (pw) => /[A-Z]/.test(pw), message: "One uppercase letter" },
        { test: (pw) => /[a-z]/.test(pw), message: "One lowercase letter" },
        { test: (pw) => /\d/.test(pw), message: "One number" },
        { test: (pw) => /[\W_]/.test(pw), message: "One special character" },
    ];

    const getPasswordErrors = () => {
        return passwordRules.filter(rule => !rule.test(password));
    };

    const isPasswordValid = () => {
        return passwordRules.every(rule => rule.test(password));
    };

    const getConfirmPasswordError = () => {
        if (!confirmPassword) return "Please confirm your password";
        if (password !== confirmPassword) return "Passwords do not match";
        return "";
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (!touched.password) {
            setTouched({ ...touched, password: true });
        }
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        if (!touched.confirmPassword) {
            setTouched({ ...touched, confirmPassword: true });
        }
    };

    async function handleSubmit(event) {
        event.preventDefault();

        // Mark all as touched
        setTouched({ password: true, confirmPassword: true });

        // Validate
        if (!isPasswordValid()) {
            toast.error("Please meet all password requirements");
            return;
        }

        if (password !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }

        setIsSubmitting(true);

        const update = { password, id, token };

        try {
            const { data } = await axios.put(`${apiUrl}/update-password`, update);

            if (data.error) {
                console.log(data);
                toast.error(data.error);
                setIsSubmitting(false);
            } else {
                setIsSuccess(true);
                toast.success("Password reset successful!");

                // Redirect after 2 seconds
                setTimeout(() => {
                    navigate("/login", { replace: true });
                }, 2000);
            }
        } catch (error) {
            console.log(error);
            toast.error(
                error.response?.data?.error ||
                    "Failed to reset password. Please try again."
            );
            setIsSubmitting(false);
        }
    }

    // Invalid token screen
    if (!isValidToken) {
        return (
            <>
                <Header />
                <div className="reset-password-page">
                    <div className="container py-5">
                        <div className="row justify-content-center">
                            <div className="col-md-6 col-lg-5">
                                <div className="error-card text-center">
                                    <ErrorOutlineIcon style={{ fontSize: "5rem", color: "#dc3545" }} />
                                    <h2 className="error-title">Invalid or Expired Link</h2>
                                    <p className="error-message">
                                        This password reset link is invalid or has expired.
                                        Password reset links are only valid for 10 minutes.
                                    </p>
                                    <button
                                        className="btn request-new-button"
                                        onClick={() => navigate("/forgotpassword")}
                                    >
                                        Request New Reset Link
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Success screen
    if (isSuccess) {
        return (
            <>
                <Header />
                <div className="reset-password-page">
                    <div className="container py-5">
                        <div className="row justify-content-center">
                            <div className="col-md-6 col-lg-5">
                                <div className="success-card text-center">
                                    <CheckCircleIcon style={{ fontSize: "5rem", color: "#4CAF50" }} />
                                    <h2 className="success-title">Password Reset Successfully!</h2>
                                    <p className="success-message">
                                        Your password has been reset successfully.
                                        You can now log in with your new password.
                                    </p>
                                    <button
                                        className="btn login-now-button"
                                        onClick={() => navigate("/login")}
                                    >
                                        Go to Login
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="reset-password-page">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-md-7 col-lg-6">
                            <div className="reset-password-card">
                                {/* Logo */}
                                <div className="text-center mb-4">
                                    <img src={logo} alt="Logo" className="reset-password-logo" />
                                </div>

                                {/* Icon */}
                                <div className="text-center mb-4">
                                    <div className="reset-password-icon">
                                        <LockResetIcon style={{ fontSize: "3rem", color: "white" }} />
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="text-center mb-4">
                                    <h2 className="reset-password-title">Create New Password</h2>
                                    <p className="reset-password-subtitle">
                                        Your new password must be different from previously used passwords.
                                    </p>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    {/* New Password */}
                                    <div className="form-group mb-3">
                                        <label htmlFor="password" className="form-label">
                                            <LockIcon fontSize="small" className="me-1" />
                                            New Password
                                        </label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className={`form-control form-control-lg ${
                                                    touched.password && !isPasswordValid() ? 'is-invalid' : ''
                                                } ${touched.password && isPasswordValid() ? 'is-valid' : ''}`}
                                                id="password"
                                                placeholder="Enter new password"
                                                value={password}
                                                onChange={handlePasswordChange}
                                                disabled={isSubmitting}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                                tabIndex="-1"
                                            >
                                                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Password Requirements */}
                                    <div className="password-requirements mb-4">
                                        <p className="requirements-title">Password must contain:</p>
                                        <ul className="requirements-list">
                                            {passwordRules.map((rule, index) => (
                                                <li
                                                    key={index}
                                                    className={`requirement-item ${
                                                        touched.password
                                                            ? rule.test(password) ? 'valid' : 'invalid'
                                                            : ''
                                                    }`}
                                                >
                                                    {touched.password && (
                                                        rule.test(password)
                                                            ? <CheckIcon className="requirement-icon" />
                                                            : <CloseIcon className="requirement-icon" />
                                                    )}
                                                    {rule.message}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="form-group mb-4">
                                        <label htmlFor="confirmPassword" className="form-label">
                                            <LockIcon fontSize="small" className="me-1" />
                                            Confirm Password
                                        </label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                className={`form-control form-control-lg ${
                                                    touched.confirmPassword && getConfirmPasswordError() ? 'is-invalid' : ''
                                                } ${touched.confirmPassword && !getConfirmPasswordError() ? 'is-valid' : ''}`}
                                                id="confirmPassword"
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={handleConfirmPasswordChange}
                                                disabled={isSubmitting}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                tabIndex="-1"
                                            >
                                                {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                            </button>
                                        </div>
                                        {touched.confirmPassword && getConfirmPasswordError() && (
                                            <div className="invalid-feedback d-block">
                                                <ErrorOutlineIcon fontSize="small" className="me-1" />
                                                {getConfirmPasswordError()}
                                            </div>
                                        )}
                                        {touched.confirmPassword && !getConfirmPasswordError() && (
                                            <div className="valid-feedback d-block">
                                                <CheckCircleIcon fontSize="small" className="me-1" />
                                                Passwords match!
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        className="btn reset-submit-button w-100"
                                        disabled={isSubmitting || !isPasswordValid() || getConfirmPasswordError()}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Resetting Password...
                                            </>
                                        ) : (
                                            <>
                                                <LockResetIcon className="me-2" />
                                                Reset Password
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            <style jsx>{`
        .reset-password-page {
          min-height: calc(100vh - 200px);
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 2rem 0;
        }

        .reset-password-card,
        .error-card,
        .success-card {
          background: white;
          border-radius: 20px;
          padding: 3rem 2.5rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .reset-password-logo {
          width: 180px;
          height: auto;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
        }

        .reset-password-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #002e6a 0%, #c70044 100%);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 25px rgba(0, 46, 106, 0.3);
        }

        .reset-password-title {
          font-size: 2rem;
          font-weight: 700;
          color: #002e6a;
          margin-bottom: 0.5rem;
        }

        .reset-password-subtitle {
          font-size: 1rem;
          color: #666;
          margin: 0;
        }

        .form-label {
          font-weight: 600;
          color: #002e6a;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
        }

        .password-input-wrapper {
          position: relative;
        }

        .form-control-lg {
          padding: 0.875rem 3rem 0.875rem 1rem;
          font-size: 1rem;
          border-radius: 10px;
          border: 2px solid #e0e0e0;
          transition: all 0.3s ease;
        }

        .form-control-lg:focus {
          border-color: #002e6a;
          box-shadow: 0 0 0 0.2rem rgba(0, 46, 106, 0.15);
        }

        .form-control-lg.is-invalid {
          border-color: #dc3545;
        }

        .form-control-lg.is-valid {
          border-color: #28a745;
        }

        .password-toggle-btn {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #666;
          cursor: pointer;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          z-index: 10;
        }

        .password-toggle-btn:hover {
          color: #002e6a;
        }

        .password-requirements {
          background: #f8f9fa;
          border-left: 4px solid #002e6a;
          padding: 1rem;
          border-radius: 8px;
        }

        .requirements-title {
          font-weight: 600;
          color: #002e6a;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
        }

        .requirements-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .requirement-item {
          padding: 0.375rem 0;
          font-size: 0.9rem;
          color: #666;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .requirement-item.valid {
          color: #28a745;
        }

        .requirement-item.invalid {
          color: #dc3545;
        }

        .requirement-icon {
          font-size: 1rem !important;
        }

        .invalid-feedback,
        .valid-feedback {
          font-size: 0.875rem;
          margin-top: 0.5rem;
          display: flex;
          align-items: center;
        }

        .invalid-feedback {
          color: #dc3545;
        }

        .valid-feedback {
          color: #28a745;
        }

        .reset-submit-button {
          background: #c70044;
          color: white;
          border: none;
          padding: 1rem 1.5rem;
          font-size: 1.1rem;
          font-weight: 600;
          border-radius: 10px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .reset-submit-button:hover:not(:disabled) {
          background: #a00036;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(199, 0, 68, 0.3);
        }

        .reset-submit-button:disabled {
          background: #cccccc;
          cursor: not-allowed;
        }

        .error-title,
        .success-title {
          font-size: 2rem;
          font-weight: 700;
          margin: 2rem 0 1rem;
        }

        .error-title {
          color: #dc3545;
        }

        .success-title {
          color: #4CAF50;
        }

        .error-message,
        .success-message {
          font-size: 1.1rem;
          color: #666;
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .request-new-button,
        .login-now-button {
          background: #002e6a;
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          font-size: 1.1rem;
          font-weight: 600;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .request-new-button:hover,
        .login-now-button:hover {
          background: #001a40;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 46, 106, 0.3);
        }

        @media (max-width: 768px) {
          .reset-password-card,
          .error-card,
          .success-card {
            padding: 2rem 1.5rem;
          }

          .reset-password-logo {
            width: 150px;
          }

          .reset-password-title,
          .error-title,
          .success-title {
            font-size: 1.75rem;
          }

          .reset-password-icon {
            width: 80px;
            height: 80px;
          }
        }

        @media (max-width: 576px) {
          .reset-password-card,
          .error-card,
          .success-card {
            padding: 1.5rem 1.25rem;
          }

          .reset-password-logo {
            width: 130px;
          }

          .reset-password-title,
          .error-title,
          .success-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
        </>
    );
}

export default ResetPassword;